import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/stores/sessionStore';
import { ToastViewport } from '@/components/ui/Toast';
import type { GetSessionResponse } from '@forest/contracts';

import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { ContactPage } from '@/pages/ContactPage';
import { ParcelsPage } from '@/pages/ParcelsPage';
import { PhotoDatabasePage } from '@/pages/PhotoDatabasePage';
import WorksPage from '@/pages/WorksPage';
import WorksReportsPage from '@/pages/WorksReportsPage';
import { ImportPage } from '@/pages/ImportPage';
import { FilterPage } from '@/pages/FilterPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { DocsPage } from '@/pages/DocsPage';
import { BetaPage } from '@/pages/BetaPage';
import { ChangelogPage } from '@/pages/ChangelogPage';
import { UsersPage } from '@/pages/admin/UsersPage';
import { AuditPage } from '@/pages/admin/AuditPage';
import { SettingsPage } from '@/pages/admin/SettingsPage';
import { RegionsImportPage } from '@/pages/admin/RegionsImportPage';
import { DownloadSettingsPage } from '@/pages/admin/DownloadSettingsPage';
import { AppCredentialsPage } from '@/pages/admin/AppCredentialsPage';
import { WorksSyncPage } from '@/pages/admin/WorksSyncPage';
import { MessagesPage } from '@/pages/admin/MessagesPage';
import { UserDetailPage } from '@/pages/admin/UserDetailPage';
import { VideosPage } from '@/pages/admin/VideosPage';
import { NotificationsAdminPage } from '@/pages/admin/NotificationsAdminPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { DownloadAppPage } from '@/pages/DownloadAppPage';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
import { AdminRoute } from '@/routes/AdminRoute';

export function App() {
  const setUser = useAuthStore((s) => s.setUser);
  const setReady = useAuthStore((s) => s.setReady);

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: async () => api.get('auth/get-session').json<GetSessionResponse>(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!isLoading) {
      setUser(data?.user ?? null);
      setReady(true);
    }
  }, [data, isLoading, setUser, setReady]);

  return (
    <>
    <ToastViewport />
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="parcels" replace />} />
        <Route path="parcels" element={<ParcelsPage />} />
        <Route path="database" element={<PhotoDatabasePage />} />
        {/* Receptions are subpolygons now, shown under their polygon. */}
        <Route path="receptii" element={<Navigate to="/app/parcels" replace />} />
        <Route
          path="works"
          element={
            <AdminRoute superOnly>
              <WorksPage />
            </AdminRoute>
          }
        />
        <Route
          path="works/reports"
          element={
            <AdminRoute superOnly>
              <WorksReportsPage />
            </AdminRoute>
          }
        />
        <Route path="import" element={<ImportPage />} />
        <Route path="filter" element={<FilterPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="download" element={<DownloadAppPage />} />
        <Route path="docs" element={<DocsPage />} />
        <Route path="beta" element={<BetaPage />} />
        <Route
          path="admin/users"
          element={
            <AdminRoute>
              <UsersPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/users/:id"
          element={
            <AdminRoute>
              <UserDetailPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/audit"
          element={
            <AdminRoute superOnly>
              <AuditPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/settings"
          element={
            <AdminRoute superOnly>
              <SettingsPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/regions"
          element={
            <AdminRoute superOnly>
              <RegionsImportPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/download"
          element={
            <AdminRoute superOnly>
              <DownloadSettingsPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/app-credentials"
          element={
            <AdminRoute superOnly>
              <AppCredentialsPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/works-sync"
          element={
            <AdminRoute superOnly>
              <WorksSyncPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/messages"
          element={
            <AdminRoute superOnly>
              <MessagesPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/videos"
          element={
            <AdminRoute superOnly>
              <VideosPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/notifications"
          element={
            <AdminRoute superOnly>
              <NotificationsAdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="changelog"
          element={
            <AdminRoute superOnly>
              <ChangelogPage />
            </AdminRoute>
          }
        />
      </Route>
      <Route path="/" element={<Navigate to="/app/parcels" replace />} />
      <Route path="*" element={<Navigate to="/app/parcels" replace />} />
    </Routes>
    </>
  );
}
